import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./css/Navbar.css";
import HamburgerMenu from "../components/HamburgerMenu";
import RegisterModal from "../components/RegisterModal";
import LoginModal from "../components/LoginModal";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/AuthContext";
import HasPermission from '../utils/HasPermission'
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';

const Navbar = () => {
  const location = useLocation();
  const {
    user,
    logout,
    isAuthenticated,
    sessionExpired,
    clearSessionExpired,
    sessionWarningVisible,
    sessionWarningCountdownSeconds,
    staySignedIn,
    isAuthInitializing,
    authInitializationError,
    retryAuthBootstrap,
  } = useContext(AuthContext);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedMobileGroups, setExpandedMobileGroups] = useState({});
  const [pendingVerificationCount, setPendingVerificationCount] = useState(0);
  const canReadAllianceManagement = HasPermission("readAllianceManagement")
  const canReadCompare = HasPermission("readCompare")
  const canReadMergerDashboard = HasPermission("readMergerDashboard")
  const canReadUsers = HasPermission("readUsers")
  const canReadEvents = HasPermission("readEvents")
  const canAccessComparePage = canReadCompare && (user?.role === 'superUser' || user?.role === 'supporter');
  const isSuperUser = user?.role === 'superUser';
  const canAccessBattleSimulation = canReadEvents && isSuperUser;

  useEffect(() => {
    if (sessionExpired) {
      setMenuOpen(false);
      setExpandedMobileGroups({});
      setIsLoginModalOpen(true);
    }
  }, [sessionExpired]);

  useEffect(() => {
    if (isAuthenticated()) {
      setIsLoginModalOpen(false);
    }
  }, [isAuthenticated, user?._id]);

  useEffect(() => {
    let isMounted = true;
    let isFetching = false;
    let lastFetchAt = 0;

    const fetchPendingCount = async ({ force = false } = {}) => {
      if (!canReadUsers || !isAuthenticated()) {
        if (isMounted) setPendingVerificationCount(0);
        return;
      }
      const now = Date.now();
      if (!force && now - lastFetchAt < 1500) {
        return;
      }
      if (isFetching) return;
      isFetching = true;
      lastFetchAt = now;
      try {
        const response = await axiosInstance.get(`${API_URL}/users/verification-requests/count`);
        if (isMounted) setPendingVerificationCount(response.data?.pendingCount || 0);
      } catch (error) {
        if (isMounted) setPendingVerificationCount(0);
      } finally {
        isFetching = false;
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        fetchPendingCount();
      }
    };

    fetchPendingCount({ force: true });
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      isMounted = false;
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [canReadUsers, isAuthenticated, user?._id]);

  const onRegisterSuccess = (userData) => {
    return;
  };

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false); // Close the modal on successful login
    clearSessionExpired();
  };

  const getPageTitle = (pathname, search = "") => {
    const tab = new URLSearchParams(search).get("tab");
    if (pathname === "/" || pathname === "/home") return "Home";
    if (pathname.startsWith("/nations")) return "Nations";
    if (pathname.startsWith("/alliances")) return "Alliances";
    if (pathname.startsWith("/commanders/review-ingestion")) return "Review Ingestion";
    if (pathname.startsWith("/commanders/history")) return "Commander History";
    if (pathname.startsWith("/commanders")) return "Commanders";
    if (pathname.startsWith("/users")) return "User Management";
    if (pathname.startsWith("/alliance-management")) {
      if (tab === "institute") return "Institute";
      return "Alliance Management";
    }
    if (pathname.startsWith("/player-management")) {
      if (tab === "strikes") return "Strikes";
      return "Player Management";
    }
    if (pathname.startsWith("/map")) return "Map";
    if (pathname.startsWith("/comparison-page")) return "Compare";
    if (pathname.startsWith("/packs") || pathname.startsWith("/tools/packs")) return "Packs";
    if (pathname.startsWith("/tools/spending-events")) return "Spending Events";
    if (pathname.startsWith("/tools/message-crafter")) return "Message Crafter";
    if (pathname.startsWith("/tools/voting")) return "Voting";
    if (pathname.startsWith("/merger-dashboard")) return "Merger Dashboard";
    if (pathname.startsWith("/admin/market")) return "Market";
    if (pathname.startsWith("/admin")) return "Admin";
    if (pathname.startsWith("/events")) return "Events";
    if (pathname.startsWith("/my-commander")) return "My Commander";
    if (pathname.startsWith("/battle-simulation/commander-profiles")) return "Commander Profiles";
    if (pathname.startsWith("/battle-simulation")) return "Battle Simulation";
    if (pathname.startsWith("/vote/")) return "Vote";
    return "AoZ Companion";
  };

  const pageTitle = getPageTitle(location.pathname, location.search);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setExpandedMobileGroups({});
  };

  const toggleMobileMenu = () => {
    setMenuOpen((isOpen) => {
      if (isOpen) setExpandedMobileGroups({});
      return !isOpen;
    });
  };

  const closeMenuAndOpenModal = (setModalOpen) => {
    closeMobileMenu();
    setModalOpen(true);
  };

  const handleLoginModalClose = () => {
    setIsLoginModalOpen(false);
    clearSessionExpired();
  };

  const closeMenuAndlogOut = () => {
    closeMobileMenu();
    logout();
  };

  const rankingsItems = [
    { label: 'Nations', to: '/nations' },
    { label: 'Alliances', to: '/alliances' },
    { label: 'Commanders', to: '/commanders' },
  ];
  const myNationItems = [
    ...(canReadAllianceManagement ? [
      { label: 'Alliance Management', to: '/alliance-management?tab=technology' },
      { label: 'Player Management', to: '/player-management' },
    ] : []),
    ...(canReadUsers ? [{ label: `Users${pendingVerificationCount > 0 ? ` (${pendingVerificationCount})` : ''}`, to: '/users' }] : []),
    { label: 'Map', to: '/map' },
    ...(canReadEvents ? [{ label: 'Events', to: '/events' }] : []),
  ];
  const toolsItems = [
    ...(isAuthenticated() ? [{ label: 'Packs', to: '/tools/packs' }] : []),
    ...(isAuthenticated() ? [{ label: 'Spending Events', to: '/tools/spending-events' }] : []),
    ...(isAuthenticated() ? [{ label: 'Message Crafter', to: '/tools/message-crafter' }] : []),
    ...(canReadAllianceManagement ? [{ label: 'Voting', to: '/tools/voting' }] : []),
    ...(canAccessComparePage ? [{ label: 'Compare', to: '/comparison-page' }] : []),
    ...(canReadMergerDashboard ? [{ label: 'Merger Dashboard', to: '/merger-dashboard' }] : []),
    ...(canAccessBattleSimulation ? [{ label: 'Battle Simulation', to: '/battle-simulation' }] : []),
  ];

  const renderNavGroup = (label, items) => {
    if (!items.length) return null;
    const isExpanded = Boolean(expandedMobileGroups[label]);
    return (
      <li className={`nav-item nav-group ${isExpanded ? "is-expanded" : ""}`}>
        <button
          type="button"
          className="nav-group-trigger"
          aria-expanded={isExpanded}
          onClick={() => setExpandedMobileGroups((prev) => ({
            ...prev,
            [label]: !prev[label],
          }))}
        >
          {label}
          <span className="nav-group-caret" aria-hidden="true">▾</span>
        </button>
        <div className="nav-group-menu">
          {items.map((item) => (
            <Link
              key={`${label}-${item.to}`}
              to={item.to}
              className={`nav-link nav-group-link ${item.nested ? 'nav-group-link--nested' : ''}`}
              onClick={closeMobileMenu}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </li>
    );
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-logo">
          <Link to="/home">AoZ Companion</Link>
          <span className="navbar-page-title">{pageTitle}</span>
        </div>

        {/* HamburgerMenu Component */}
        <HamburgerMenu isOpen={menuOpen} toggleMenu={toggleMobileMenu} />

        <div className={`navbar-menu ${menuOpen ? "open" : ""}`}>
          <ul className="nav-list">
            {renderNavGroup('Rankings', rankingsItems)}
            {renderNavGroup('My Nation', myNationItems)}
            {isAuthenticated() && (
              <li className="nav-item">
                <Link to="/my-commander" className="nav-link" onClick={closeMobileMenu}>
                  My Commander
                </Link>
              </li>
            )}
            {renderNavGroup('Tools', toolsItems)}
            {isSuperUser && (
              <li className="nav-item">
                <Link to="/admin" className="nav-link" onClick={closeMobileMenu}>
                  Admin
                </Link>
              </li>
            )}
          </ul>

          <div className="auth-button-container">
            {isAuthInitializing ? (
              <>
                <span className="auth-status-text">Checking session...</span>
                <button onClick={() => closeMenuAndOpenModal(setIsLoginModalOpen)}>
                  Login
                </button>
              </>
            ) : authInitializationError && !isAuthenticated() ? (
              <>
                <span className="auth-status-text auth-status-text-warning">
                  {authInitializationError}
                </span>
                <button onClick={retryAuthBootstrap}>Retry</button>
                <button onClick={() => closeMenuAndOpenModal(setIsLoginModalOpen)}>
                  Login
                </button>
              </>
            ) : !isAuthenticated() ? (
              <>
                <button onClick={() => closeMenuAndOpenModal(setIsRegisterModalOpen)}>
                  Register
                </button>
                <button onClick={() => closeMenuAndOpenModal(setIsLoginModalOpen)}>
                  Login
                </button>
              </>
            ) : (
              <>
                <button
                  className={user?.role === 'user' ? 'verify-profile-button-highlight' : ''}
                  onClick={() => closeMenuAndOpenModal(setIsUserModalOpen)}
                >
                  {user.username}
                </button>
                {user?.role === 'user' && (
                  <span className="verify-profile-prompt">Click your name to verify</span>
                )}
                <button onClick={closeMenuAndlogOut}>Logout</button>
              </>
            )}
          </div>
        </div>
      </div>

      <RegisterModal
        isOpen={isRegisterModalOpen}
        onRegisterSuccess={onRegisterSuccess}
        onRequestClose={() => setIsRegisterModalOpen(false)}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onLoginSuccess={handleLoginSuccess}
        onRequestClose={handleLoginModalClose}
      />
      <UserModal
        user={user}
        isOpen={isUserModalOpen}
        onRequestClose={() => setIsUserModalOpen(false)}
      />
      {sessionWarningVisible && isAuthenticated() && (
        <div className="session-warning-overlay" role="dialog" aria-modal="true" aria-labelledby="session-warning-title">
          <div className="session-warning-modal">
            <h3 id="session-warning-title">Still there?</h3>
            <p>Your session will expire in {sessionWarningCountdownSeconds}s due to inactivity.</p>
            <div className="session-warning-actions">
              <button type="button" onClick={staySignedIn}>Stay signed in</button>
              <button type="button" onClick={closeMenuAndlogOut}>Log out</button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
